/**
 * Partnerske ponude - platformski deo programa (ADR-071).
 *
 * Ponude i mreže su globalne; procenat provizije koji ide lokalu određuje
 * administrator platforme po ponudi (D1) i snima se na tokenu pri izdavanju,
 * pa izmena procenta važi samo za nove prikaze QR koda.
 *
 * Novac u isplati sme da potiče samo iz prodaja koje je mreža stvarno platila
 * (D6) - zato postoji ručno „Označi naplaćeno od mreže" na karticu Prodaje.
 * Kurs se unosi jednom po danu i ne prepisuje se: iz njega je već izvedena
 * pripisana zarada.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, Info, KeyRound, Pencil, Plus, Power, RefreshCw, Save, Trash2 } from 'lucide-react';
import { api } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { AffiliateGuide, type GuideTarget } from './AffiliateGuide';
import {
  type AffiliateSettings, type ConversionStatus, type ConversionView, type FxRate, type IsplataRadnja,
  type NetworkView, type OfferKind, type OfferView, type PayoutView, type PostbackMapping, type RequestRow, type RewardMode,
  type Valuta, Dijalog, DugmadDijaloga, IsplataPotvrda, IsplateTabela, IzborNacina, KopirajDugme, NACIN_NAGRADE, Oznaka, Polje,
  ProdajeTabela, STATUS_PRODAJE, STATUS_PROGRAMA, VALUTE, btnDangerSecondary, btnPrimary, btnSecondary, btnIcon, card,
  errorBox, input, mono, mozeDaMenja, napomena, okBox, porukaGreske, prazno, proveriAdresu, rsd, tabela, td, th,
  trenutak, datum, warnBox,
} from './shared';

type Kartica = 'guide' | GuideTarget;

/** `opis` je jedna rečenica o tome čemu kartica služi - prikazuje se iznad nje. */
const KARTICE: Array<{ key: Kartica; label: string; opis?: string }> = [
  { key: 'guide', label: 'Kako radi' },
  { key: 'offers', label: 'Ponude', opis: 'Šta lokali nude kupcima i koliki deo provizije dobijaju. Izmena procenta važi samo za QR kodove izdate posle nje.' },
  { key: 'networks', label: 'Mreže', opis: 'Partneri sa kojima Efikasa ima ugovor: da li i kuda javljaju prodaje i kako se čitaju njihova polja.' },
  { key: 'requests', label: 'Zahtevi', opis: 'Lokali koji su prihvatili uslove i traže pristup programu. Razlog odbijanja lokal vidi.' },
  { key: 'conversions', label: 'Prodaje', opis: 'Sve što su mreže javile. Ovde označavaš prodaje koje je mreža stvarno platila - tek one mogu u isplatu u novcu.' },
  { key: 'payouts', label: 'Isplate', opis: 'Zahtevi lokala za isplatu u novcu: prihvati, uplati, pa označi plaćeno sa referencom uplate.' },
  { key: 'fx', label: 'Kursevi', opis: 'Srednji kurs NBS za EUR i USD - server ga preuzima sam, a može se uneti i ručno. Bez kursa se prodaja ne pripisuje; kurs se ne prepisuje.' },
  { key: 'settings', label: 'Podešavanja', opis: 'Prag za isplatu u novcu i adrese programa. Način nagrade (krediti ili novac) se bira za svaki lokal posebno.' },
];

export function AffiliatePage() {
  const [kartica, setKartica] = useState<Kartica>('guide');
  const admin = mozeDaMenja();
  const opis = KARTICE.find((k) => k.key === kartica)?.opis;

  return (
    <div>
      <header style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Partnerske ponude</h1>
        <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
          Ponude affiliate mreža koje lokali prikazuju kupcima · deo provizije ide lokalu kao umanjenje pretplate ili u novcu
          {!admin && ' · pregled bez izmena (menja samo administrator platforme)'}
        </div>
      </header>

      <div style={{ borderBottom: '1px solid var(--color-border)', marginBottom: 20, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {KARTICE.map((k) => (
          <button
            key={k.key}
            onClick={() => setKartica(k.key)}
            data-testid={`affiliate-tab-${k.key}`}
            style={{
              padding: '10px 18px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', background: 'transparent',
              color: kartica === k.key ? 'var(--color-text-strong)' : 'var(--color-text-muted)',
              fontSize: 13, fontWeight: kartica === k.key ? 600 : 500,
              borderBottom: kartica === k.key ? '2px solid var(--color-primary)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {k.label}
          </button>
        ))}
      </div>

      {opis && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 13, color: 'var(--color-text-muted)' }}>
          <Info size={16} style={{ flexShrink: 0 }} />
          <span>{opis}</span>
          <button
            onClick={() => setKartica('guide')}
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-primary)', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}
          >
            Kako radi →
          </button>
        </div>
      )}

      {kartica === 'guide' && (
        <AffiliateGuide onIdi={(t) => { setKartica(t); window.scrollTo({ top: 0 }); document.querySelector('main')?.scrollTo({ top: 0 }); }} />
      )}
      {kartica === 'offers' && <PonudeKartica admin={admin} />}
      {kartica === 'networks' && <MrezeKartica admin={admin} />}
      {kartica === 'requests' && <ZahteviKartica admin={admin} />}
      {kartica === 'conversions' && <ProdajeKartica admin={admin} />}
      {kartica === 'payouts' && <IsplateKartica admin={admin} />}
      {kartica === 'fx' && <KurseviKartica admin={admin} />}
      {kartica === 'settings' && <PodesavanjaKartica admin={admin} />}
    </div>
  );
}

/** Zajedničko učitavanje sa greškom i porukom o uspehu. */
function useUcitavanje<T>(ucitaj: () => Promise<T>) {
  const [podaci, setPodaci] = useState<T | null>(null);
  const [greska, setGreska] = useState<string | null>(null);
  const [poruka, setPoruka] = useState<string | null>(null);
  const [radi, setRadi] = useState(false);
  const osvezi = useCallback(async () => {
    setRadi(true);
    setGreska(null);
    try {
      setPodaci(await ucitaj());
    } catch (e) {
      setGreska(porukaGreske(e, 'Učitavanje nije uspelo.'));
    } finally {
      setRadi(false);
    }
  }, [ucitaj]);
  useEffect(() => { void osvezi(); }, [osvezi]);
  return { podaci, greska, setGreska, poruka, setPoruka, radi, osvezi };
}

function Traka({ children, onOsvezi, radi }: { children?: React.ReactNode; onOsvezi: () => void; radi: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
      {children}
      <div style={{ flex: 1 }} />
      <button onClick={onOsvezi} disabled={radi} style={btnSecondary}><RefreshCw size={16} /> Osveži</button>
    </div>
  );
}

function Poruke({ greska, poruka }: { greska: string | null; poruka: string | null }) {
  return (
    <>
      {greska && <div style={errorBox}><AlertTriangle size={16} /> {greska}</div>}
      {poruka && <div style={okBox}><Check size={16} /> {poruka}</div>}
    </>
  );
}

// ═════════════════════════ Ponude ═════════════════════════

function PonudeKartica({ admin }: { admin: boolean }) {
  const ucitaj = useCallback(async () => {
    const [o, n] = await Promise.all([
      api.get<{ offers: OfferView[] }>('/admin/affiliate/offers'),
      api.get<{ networks: NetworkView[] }>('/admin/affiliate/networks'),
    ]);
    return { ponude: o.offers, mreze: n.networks };
  }, []);
  const s = useUcitavanje(ucitaj);
  const [forma, setForma] = useState<OfferView | 'nova' | null>(null);
  const [gasenje, setGasenje] = useState<OfferView | null>(null);

  const ponovoUkljuci = async (o: OfferView) => {
    s.setGreska(null);
    try {
      await api.put(`/admin/affiliate/offers/${o.id}`, { isActive: true });
      s.setPoruka(`Ponuda „${o.title}" je ponovo aktivna.`);
      await s.osvezi();
    } catch (e) {
      s.setGreska(porukaGreske(e));
    }
  };

  const mreze = s.podaci?.mreze ?? [];
  const ponude = s.podaci?.ponude ?? [];

  return (
    <div>
      <Traka onOsvezi={() => void s.osvezi()} radi={s.radi}>
        {admin && (
          <button onClick={() => setForma('nova')} disabled={mreze.length === 0} style={{ ...btnPrimary, opacity: mreze.length === 0 ? 0.5 : 1 }} data-testid="affiliate-nova-ponuda">
            <Plus size={16} /> Nova ponuda
          </button>
        )}
        {admin && s.podaci && mreze.length === 0 && (
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Prvo dodajte mrežu na kartici Mreže.</span>
        )}
      </Traka>
      <Poruke greska={s.greska} poruka={s.poruka} />

      <div style={card}>
        {ponude.length === 0 ? (
          <div style={prazno}>{s.radi ? 'Učitavanje...' : 'Još nema nijedne ponude.'}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tabela}>
              <thead>
                <tr>
                  <th style={th}>Redosled</th>
                  <th style={th}>Naslov</th>
                  <th style={th}>Mreža</th>
                  <th style={{ ...th, textAlign: 'right' }}>Procenat lokala</th>
                  <th style={{ ...th, textAlign: 'right' }}>Procena po kupovini</th>
                  <th style={th}>Adresa</th>
                  <th style={th}>Stanje</th>
                  {admin && <th style={{ ...th, textAlign: 'right' }}>Radnje</th>}
                </tr>
              </thead>
              <tbody>
                {ponude.map((o) => (
                  <tr key={o.id} data-testid={`affiliate-ponuda-${o.id}`}>
                    <td style={td}>{o.sortOrder}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: 'var(--color-text-strong)' }}>{o.title}</div>
                      {o.customerDescription && <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>{o.customerDescription}</div>}
                    </td>
                    <td style={td}>{o.networkName ?? mreze.find((m) => m.id === o.networkId)?.name ?? '-'}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{o.tenantSharePercent} %</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{rsd(o.estimateRsdPerPurchase)}</td>
                    <td style={{ ...td, ...(o.kind === 'tenant_link' ? {} : mono), maxWidth: 280 }}>
                      {o.kind === 'tenant_link'
                        ? <>Poseban link za svaki lokal<div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>link ima {o.tenantLinkCount ?? 0} lok.</div></>
                        : o.urlTemplate}
                    </td>
                    <td style={td}><Oznaka ton={o.isActive ? 'ok' : 'muted'}>{o.isActive ? 'aktivna' : 'ugašena'}</Oznaka></td>
                    {admin && (
                      <td style={{ ...td, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 4 }}>
                          <button onClick={() => setForma(o)} title="Izmeni ponudu" style={btnIcon}><Pencil size={16} /></button>
                          {o.isActive
                            ? <button onClick={() => setGasenje(o)} title="Ugasi ponudu" style={btnIcon}><Power size={16} /></button>
                            : <button onClick={() => void ponovoUkljuci(o)} title="Ponovo uključi ponudu" style={{ ...btnIcon, color: '#86efac' }}><Power size={16} /></button>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {forma && (
        <PonudaForma
          ponuda={forma === 'nova' ? null : forma}
          mreze={mreze}
          onZatvori={() => setForma(null)}
          onGotovo={(p) => { setForma(null); s.setPoruka(p); void s.osvezi(); }}
        />
      )}

      {gasenje && (
        <ConfirmDialog
          title="Ugasi ponudu"
          message={`Ugasiti ponudu „${gasenje.title}"? Lokali je više ne vide, a već prikazani QR kodovi vode na stranu „Ponuda više nije dostupna". Ponuda se ne briše - već zabeležene prodaje ostaju.`}
          confirmLabel="Ugasi"
          onConfirm={async () => {
            try {
              await api.post(`/admin/affiliate/offers/${gasenje.id}/deactivate`, {});
            } catch (e) {
              throw new Error(porukaGreske(e));
            }
            s.setPoruka(`Ponuda „${gasenje.title}" je ugašena.`);
            void s.osvezi();
          }}
          onClose={() => setGasenje(null)}
        />
      )}
    </div>
  );
}

function PonudaForma({ ponuda, mreze, onZatvori, onGotovo }: {
  ponuda: OfferView | null; mreze: NetworkView[]; onZatvori: () => void; onGotovo: (poruka: string) => void;
}) {
  const [networkId, setNetworkId] = useState(ponuda?.networkId ?? mreze.find((m) => m.isActive)?.id ?? mreze[0]?.id ?? '');
  const mreza = mreze.find((m) => m.id === networkId);
  const mrezaBezPotvrda = !!mreza && !mreza.postbackMapping;
  const [vrsta, setVrsta] = useState<OfferKind>(ponuda?.kind ?? (mrezaBezPotvrda ? 'tenant_link' : 'postback'));
  // Mreža koja ne šalje potvrde ume samo link po lokalu (server isto odbija).
  const kind: OfferKind = ponuda ? ponuda.kind : mrezaBezPotvrda ? 'tenant_link' : vrsta;
  const [title, setTitle] = useState(ponuda?.title ?? '');
  const [urlTemplate, setUrlTemplate] = useState(ponuda?.urlTemplate ?? '');
  const [procenat, setProcenat] = useState(ponuda?.tenantSharePercent ?? '');
  const [procena, setProcena] = useState(ponuda?.estimateRsdPerPurchase ?? '');
  const [opis, setOpis] = useState(ponuda?.customerDescription ?? '');
  const [listic, setListic] = useState(ponuda?.slipText ?? '');
  const [redosled, setRedosled] = useState(String(ponuda?.sortOrder ?? 0));
  const [aktivna, setAktivna] = useState(ponuda?.isActive ?? true);
  const [greska, setGreska] = useState<string | null>(null);
  const [radi, setRadi] = useState(false);

  const greskaAdrese = kind === 'postback' && urlTemplate.trim() ? proveriAdresu(urlTemplate) : null;
  const procenatBroj = Number(procenat.replace(',', '.'));
  const greskaProcenta = procenat.trim() === '' ? null
    : !Number.isFinite(procenatBroj) || procenatBroj < 0 || procenatBroj > 100 ? 'Procenat lokala mora biti od 0 do 100.' : null;
  const procenaBroj = procena.trim() === '' ? null : Number(procena.replace(',', '.'));
  const greskaProcene = procenaBroj !== null && (!Number.isFinite(procenaBroj) || procenaBroj < 0) ? 'Procena mora biti pozitivan iznos.' : null;
  const redosledBroj = Number.parseInt(redosled, 10);
  const spremno = !!networkId && !!title.trim() && (kind === 'tenant_link' || !!urlTemplate.trim()) && procenat.trim() !== ''
    && !greskaAdrese && !greskaProcenta && !greskaProcene && Number.isFinite(redosledBroj);

  const sacuvaj = async () => {
    if (!spremno) return;
    setRadi(true);
    setGreska(null);
    const telo = {
      title: title.trim(),
      ...(kind === 'postback' ? { urlTemplate: urlTemplate.trim() } : {}),
      tenantSharePercent: procenat.trim().replace(',', '.'),
      estimateRsdPerPurchase: procenaBroj,
      customerDescription: opis,
      slipText: listic,
      sortOrder: redosledBroj,
      isActive: aktivna,
    };
    try {
      if (ponuda) {
        await api.put(`/admin/affiliate/offers/${ponuda.id}`, telo);
        onGotovo(ponuda.tenantSharePercent !== procenatBroj.toFixed(2)
          ? 'Ponuda je izmenjena. Novi procenat važi za nove prikaze QR koda; već izdati zadržavaju stari.'
          : 'Ponuda je izmenjena.');
      } else {
        await api.post('/admin/affiliate/offers', { networkId, kind, ...telo });
        onGotovo(kind === 'tenant_link'
          ? 'Ponuda je dodata. Lokali je vide tek kad im upišete link (Klijenti → firma → Partnerske ponude → Linkovi za ponude).'
          : 'Ponuda je dodata.');
      }
    } catch (e) {
      setGreska(porukaGreske(e, 'Ponuda nije sačuvana.'));
    } finally {
      setRadi(false);
    }
  };

  return (
    <Dijalog naslov={ponuda ? 'Izmena ponude' : 'Nova ponuda'} onZatvori={onZatvori} sirina={620}>
      {greska && <div style={errorBox}><AlertTriangle size={16} /> {greska}</div>}

      <Polje label="Mreža" pomoc={ponuda ? 'Mreža se posle dodavanja ponude ne menja.' : undefined}>
        <select value={networkId} onChange={(e) => setNetworkId(e.target.value)} disabled={!!ponuda} style={input}>
          {mreze.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}{m.postbackMapping ? '' : ' (bez potvrda)'}{m.isActive ? '' : ' (neaktivna)'}
            </option>
          ))}
        </select>
      </Polje>

      <Polje
        label="Kako se zna koji lokal je doneo prodaju"
        pomoc={ponuda ? 'Vrsta se posle dodavanja ne menja - izdati QR kodovi vode po njoj.'
          : mrezaBezPotvrda ? 'Ova mreža ne šalje potvrde, pa je moguć samo poseban link za svaki lokal.' : undefined}
      >
        <div style={{ display: 'grid', gap: 6 }}>
          {([
            { k: 'postback', naslov: 'Mreža javlja prodaju (postback)', opis: 'Jedan link za sve lokale, sa {click_id}. Mreža sama javi prodaju i Efikasa je pripiše lokalu.' },
            { k: 'tenant_link', naslov: 'Poseban link za svaki lokal', opis: 'Partner nema potvrde, ali daje poseban link (ili kod) za svaki lokal i izveštava po njemu. Zaradu unosite ručno, sa referencom uplate.' },
          ] as Array<{ k: OfferKind; naslov: string; opis: string }>).map((o) => {
            const off = !!ponuda || (o.k === 'postback' && mrezaBezPotvrda);
            return (
              <label key={o.k} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, opacity: off && kind !== o.k ? 0.5 : 1, cursor: off ? 'default' : 'pointer' }}>
                <input type="radio" name="vrsta-ponude" checked={kind === o.k} disabled={off} onChange={() => setVrsta(o.k)} style={{ marginTop: 3 }} />
                <span>
                  <strong style={{ color: 'var(--color-text-strong)' }}>{o.naslov}</strong>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)' }}>{o.opis}</span>
                </span>
              </label>
            );
          })}
        </div>
      </Polje>

      <Polje label="Naslov">
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} style={input} data-testid="ponuda-naslov" />
      </Polje>

      {kind === 'postback' ? (
        <Polje label="Adresa ponude" pomoc="Samo https. Mora sadržati {click_id} - tu Efikasa upisuje oznaku skena po kojoj mreža javlja prodaju.">
          <input
            value={urlTemplate}
            onChange={(e) => setUrlTemplate(e.target.value)}
            placeholder="https://partner.example/ponuda?sub_id={click_id}"
            style={{ ...input, ...mono, borderColor: greskaAdrese ? '#ef4444' : 'var(--color-border)' }}
            data-testid="ponuda-adresa"
          />
          {greskaAdrese && <div style={{ fontSize: 12, color: '#fca5a5', marginTop: 4 }}>{greskaAdrese}</div>}
        </Polje>
      ) : (
        <div style={{ ...napomena, marginBottom: 12 }}>
          Link se upisuje za svaki lokal posebno: Klijenti → firma → Partnerske ponude → <strong>Linkovi za ponude</strong>.
          Lokal ponudu vidi tek kad ima svoj link.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <Polje label="Procenat lokala (%)" pomoc={kind === 'tenant_link' ? 'Deo provizije koji ide lokalu; primenjuje se na iznos koji unesete ručno.' : 'Deo provizije mreže koji ide lokalu.'}>
          <input
            value={procenat}
            onChange={(e) => setProcenat(e.target.value)}
            inputMode="decimal"
            placeholder="npr. 50"
            style={{ ...input, borderColor: greskaProcenta ? '#ef4444' : 'var(--color-border)' }}
            data-testid="ponuda-procenat"
          />
          {greskaProcenta && <div style={{ fontSize: 12, color: '#fca5a5', marginTop: 4 }}>{greskaProcenta}</div>}
        </Polje>
        <Polje label="Procena po kupovini (RSD)" pomoc="Okvirna zarada lokala; vide je samo vlasnik i menadžer.">
          <input
            value={procena}
            onChange={(e) => setProcena(e.target.value)}
            inputMode="decimal"
            placeholder="nije obavezno"
            style={{ ...input, borderColor: greskaProcene ? '#ef4444' : 'var(--color-border)' }}
          />
          {greskaProcene && <div style={{ fontSize: 12, color: '#fca5a5', marginTop: 4 }}>{greskaProcene}</div>}
        </Polje>
        <Polje label="Redosled">
          <input value={redosled} onChange={(e) => setRedosled(e.target.value)} inputMode="numeric" style={input} />
        </Polje>
      </div>

      <Polje label="Opis za kupca" pomoc="Prikazuje se na kartici ponude i uz QR kod.">
        <textarea value={opis} onChange={(e) => setOpis(e.target.value)} rows={3} maxLength={2000} style={{ ...input, fontFamily: 'inherit', resize: 'vertical' }} />
      </Polje>

      <Polje label="Tekst na listiću" pomoc="Štampa se ispod QR koda na nefiskalnom listiću.">
        <textarea value={listic} onChange={(e) => setListic(e.target.value)} rows={2} maxLength={1000} style={{ ...input, fontFamily: 'inherit', resize: 'vertical' }} />
      </Polje>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8 }}>
        <input type="checkbox" checked={aktivna} onChange={(e) => setAktivna(e.target.checked)} />
        Aktivna (lokali je vide)
      </label>

      {ponuda && (
        <div style={napomena}>
          Procenat se snima na svakom prikazu QR koda. Izmena važi samo za nove prikaze; prodaje sa već izdatih
          kodova se obračunavaju po starom procentu.
        </div>
      )}

      <DugmadDijaloga onOtkazi={onZatvori} onPotvrdi={() => void sacuvaj()} radi={radi} onemoguceno={!spremno} />
    </Dijalog>
  );
}

// ═════════════════════════ Mreže ═════════════════════════

type Tajna = { naslov: string; secret: string; postbackUrl: string };

function MrezeKartica({ admin }: { admin: boolean }) {
  const ucitaj = useCallback(async () => (await api.get<{ networks: NetworkView[] }>('/admin/affiliate/networks')).networks, []);
  const s = useUcitavanje(ucitaj);
  const [forma, setForma] = useState<NetworkView | 'nova' | null>(null);
  const [rotacija, setRotacija] = useState<NetworkView | null>(null);
  const [tajna, setTajna] = useState<Tajna | null>(null);

  const mreze = s.podaci ?? [];

  return (
    <div>
      <Traka onOsvezi={() => void s.osvezi()} radi={s.radi}>
        {admin && <button onClick={() => setForma('nova')} style={btnPrimary} data-testid="affiliate-nova-mreza"><Plus size={16} /> Nova mreža</button>}
      </Traka>
      <Poruke greska={s.greska} poruka={s.poruka} />

      <div style={card}>
        {mreze.length === 0 ? (
          <div style={prazno}>{s.radi ? 'Učitavanje...' : 'Još nema nijedne mreže.'}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tabela}>
              <thead>
                <tr>
                  <th style={th}>Naziv</th>
                  <th style={th}>Oznaka</th>
                  <th style={th}>Valuta</th>
                  <th style={th}>Mapiranje polja</th>
                  <th style={th}>Adresa za potvrde</th>
                  <th style={th}>Stanje</th>
                  {admin && <th style={{ ...th, textAlign: 'right' }}>Radnje</th>}
                </tr>
              </thead>
              <tbody>
                {mreze.map((m) => (
                  <tr key={m.id}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: 'var(--color-text-strong)' }}>{m.name}</div>
                      {m.notes && <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>{m.notes}</div>}
                    </td>
                    <td style={{ ...td, ...mono }}>{m.slug}</td>
                    <td style={td}>{m.defaultCurrency}</td>
                    <td style={{ ...td, fontSize: 12 }}>
                      {m.postbackMapping ? (
                        <>
                          <div>klik: <code>{m.postbackMapping.clickId}</code> · transakcija: <code>{m.postbackMapping.txId}</code> · stanje: <code>{m.postbackMapping.status}</code></div>
                          {(m.postbackMapping.amount || m.postbackMapping.currency || m.postbackMapping.occurredAt) && (
                            <div style={{ color: 'var(--color-text-muted)' }}>
                              {m.postbackMapping.amount && <>iznos: <code>{m.postbackMapping.amount}</code> </>}
                              {m.postbackMapping.currency && <>valuta: <code>{m.postbackMapping.currency}</code> </>}
                              {m.postbackMapping.occurredAt && <>vreme: <code>{m.postbackMapping.occurredAt}</code></>}
                            </div>
                          )}
                          <div style={{ color: 'var(--color-text-muted)' }}>
                            {Object.entries(m.postbackMapping.statusMap).map(([k, v]) => `${k} → ${STATUS_PRODAJE[v]?.label ?? v}`).join(' · ')}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>Ne šalje potvrde - zarada se unosi ručno po lokalu.</span>
                      )}
                    </td>
                    <td style={{ ...td, ...mono, maxWidth: 260 }}>{m.postbackMapping ? m.postbackUrlTemplate : '-'}</td>
                    <td style={td}><Oznaka ton={m.isActive ? 'ok' : 'muted'}>{m.isActive ? 'aktivna' : 'neaktivna'}</Oznaka></td>
                    {admin && (
                      <td style={{ ...td, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 4 }}>
                          <button onClick={() => setForma(m)} title="Izmeni mrežu" style={btnIcon}><Pencil size={16} /></button>
                          {m.postbackMapping && <button onClick={() => setRotacija(m)} title="Nova tajna za potvrde" style={btnIcon}><KeyRound size={16} /></button>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {forma && (
        <MrezaForma
          mreza={forma === 'nova' ? null : forma}
          onZatvori={() => setForma(null)}
          onGotovo={(poruka, novaTajna) => {
            setForma(null);
            s.setPoruka(poruka);
            if (novaTajna) setTajna(novaTajna);
            void s.osvezi();
          }}
        />
      )}

      {rotacija && (
        <ConfirmDialog
          title="Nova tajna za potvrde"
          message={`Napraviti novu tajnu za mrežu „${rotacija.name}"? Stara adresa za potvrde odmah prestaje da važi - dok mreži ne pošaljete novu adresu, njene potvrde prodaje biće odbijene.`}
          confirmLabel="Napravi novu tajnu"
          onConfirm={async () => {
            let r: { network: NetworkView; secret: string; postbackUrl: string };
            try {
              r = await api.post(`/admin/affiliate/networks/${rotacija.id}/rotate-secret`, {});
            } catch (e) {
              throw new Error(porukaGreske(e));
            }
            setTajna({ naslov: `Nova adresa za potvrde - ${r.network.name}`, secret: r.secret, postbackUrl: r.postbackUrl });
            s.setPoruka('Tajna je zamenjena. Stara adresa više ne važi.');
            void s.osvezi();
          }}
          onClose={() => setRotacija(null)}
        />
      )}

      {tajna && <TajnaDijalog tajna={tajna} onZatvori={() => setTajna(null)} />}
    </div>
  );
}

/** Tajna se ne čuva - posle zatvaranja ovog prozora više je niko ne može videti. */
function TajnaDijalog({ tajna, onZatvori }: { tajna: Tajna; onZatvori: () => void }) {
  return (
    <Dijalog naslov={tajna.naslov} onZatvori={onZatvori} sirina={640}>
      <div style={warnBox}>
        <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          Ova adresa se prikazuje <strong>samo sada</strong>. Kopirajte je i unesite u podešavanja mreže (postback URL).
          Efikasa čuva samo otisak tajne - posle zatvaranja prozora adresa se ne može ponovo prikazati, samo zameniti novom.
        </div>
      </div>

      <Polje label="Pun postback URL">
        <div style={{ ...napomena, ...mono, color: 'var(--color-text-strong)', marginBottom: 8 }} data-testid="affiliate-postback-url">{tajna.postbackUrl}</div>
        <KopirajDugme tekst={tajna.postbackUrl} label="Kopiraj adresu" />
      </Polje>

      <Polje label="Tajna">
        <div style={{ ...napomena, ...mono, color: 'var(--color-text-strong)', marginBottom: 8 }}>{tajna.secret}</div>
        <KopirajDugme tekst={tajna.secret} label="Kopiraj tajnu" />
      </Polje>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
        <button onClick={onZatvori} style={btnPrimary}>Sačuvao sam adresu</button>
      </div>
    </Dijalog>
  );
}

type RedStatusa = { kljuc: number; spolja: string; nas: ConversionStatus };

/** Šta svako polje potvrde znači - prikazuje se ispod polja (ADR-078). */
const POMOC_POLJA = {
  clickId: 'Ime parametra u kome mreža vraća naš kod klika (kod Travelpayouts-a SubID). Bez njega ne znamo koji lokal je doneo prodaju.',
  txId: 'Ime parametra sa brojem prodaje kod mreže. Po njemu prepoznajemo istu prodaju kad mreža pošalje potvrdu ponovo.',
  status: 'Ime parametra sa stanjem prodaje (na čekanju, potvrđena, otkazana). Vrednosti se prevode u „Mapi stanja" ispod.',
  amount: 'Ime parametra sa našom zaradom (provizijom), ne cenom koju je kupac platio. Bez njega prodaja nema iznos.',
  currency: 'Ime parametra sa valutom provizije. Bez njega važi valuta mreže iznad. Prima se samo RSD, EUR ili USD.',
  occurredAt: 'Ime parametra sa datumom prodaje (Unix sekunde ili ISO datum). Određuje kurs; bez njega važi vreme prijema.',
};

type ProbaRezultat = {
  ok: boolean;
  error?: string;
  message?: string;
  params: Record<string, string>;
  parsed?: { clickId: string; txId: string; status: ConversionStatus; amount: string | null; currency: string | null; occurredAt: string | null };
  currencySupported?: boolean;
  match?: { tenantName: string; offerTitle: string } | null;
};

const GRESKE_PROBE: Record<string, string> = {
  SAMPLE_UNREADABLE: 'Primer nije adresa, upit (a=1&b=2) ni JSON objekat.',
  MISSING_CLICK_ID: 'U primeru nema parametra za oznaku klika - proverite ime polja „Oznaka klika".',
  MISSING_TX_ID: 'U primeru nema parametra za transakciju - proverite ime polja „Transakcija".',
  MISSING_STATUS: 'U primeru nema parametra za stanje - proverite ime polja „Stanje".',
  UNKNOWN_STATUS: 'Vrednost stanja iz primera nije u Mapi stanja - dodajte je, inače se potvrda odbija.',
  INVALID_AMOUNT: 'Iznos provizije nije broj (dozvoljeno do 4 decimale, zarez ili tačka).',
  INVALID_CURRENCY: 'Valuta u primeru nije troslovna oznaka.',
};

function MrezaForma({ mreza, onZatvori, onGotovo }: {
  mreza: NetworkView | null; onZatvori: () => void; onGotovo: (poruka: string, tajna?: Tajna) => void;
}) {
  const m = mreza?.postbackMapping;
  const [name, setName] = useState(mreza?.name ?? '');
  const [slug, setSlug] = useState(mreza?.slug ?? '');
  const [valuta, setValuta] = useState<Valuta>(((VALUTE as readonly string[]).includes(mreza?.defaultCurrency ?? '') ? mreza?.defaultCurrency : 'RSD') as Valuta);
  const [notes, setNotes] = useState(mreza?.notes ?? '');
  const [aktivna, setAktivna] = useState(mreza?.isActive ?? true);
  const [saljePotvrde, setSaljePotvrde] = useState(mreza ? !!m : true);
  const [clickId, setClickId] = useState(m?.clickId ?? 'click_id');
  const [txId, setTxId] = useState(m?.txId ?? 'transaction_id');
  const [status, setStatus] = useState(m?.status ?? 'status');
  const [amount, setAmount] = useState(m?.amount ?? '');
  const [currency, setCurrency] = useState(m?.currency ?? '');
  const [occurredAt, setOccurredAt] = useState(m?.occurredAt ?? '');
  const [redovi, setRedovi] = useState<RedStatusa[]>(() => {
    const ulaz = m ? Object.entries(m.statusMap) : [['pending', 'pending'], ['approved', 'approved'], ['rejected', 'reversed']] as Array<[string, ConversionStatus]>;
    return ulaz.map(([spolja, nas], i) => ({ kljuc: i, spolja, nas: nas as ConversionStatus }));
  });
  const [primer, setPrimer] = useState('');
  const [proba, setProba] = useState<ProbaRezultat | null>(null);
  const [probaRadi, setProbaRadi] = useState(false);
  const [greska, setGreska] = useState<string | null>(null);
  const [radi, setRadi] = useState(false);

  const slugOk = /^[a-z0-9-]{2,40}$/.test(slug.trim());
  const popunjeniRedovi = redovi.filter((r) => r.spolja.trim());
  const dupli = new Set(popunjeniRedovi.map((r) => r.spolja.trim().toLowerCase())).size !== popunjeniRedovi.length;
  const mapiranjeOk = !!clickId.trim() && !!txId.trim() && !!status.trim() && popunjeniRedovi.length > 0 && !dupli;
  const spremno = !!name.trim() && (!!mreza || slugOk) && (!saljePotvrde || mapiranjeOk);

  const mapiranje = (): PostbackMapping => ({
    clickId: clickId.trim(),
    txId: txId.trim(),
    status: status.trim(),
    ...(amount.trim() ? { amount: amount.trim() } : {}),
    ...(currency.trim() ? { currency: currency.trim() } : {}),
    ...(occurredAt.trim() ? { occurredAt: occurredAt.trim() } : {}),
    statusMap: Object.fromEntries(popunjeniRedovi.map((r) => [r.spolja.trim(), r.nas])),
  });

  const proveri = async () => {
    if (!mapiranjeOk || !primer.trim()) return;
    setProbaRadi(true);
    setProba(null);
    try {
      setProba(await api.post<ProbaRezultat>('/admin/affiliate/networks/test-postback', {
        postbackMapping: mapiranje(), defaultCurrency: valuta, sample: primer,
      }));
    } catch (e) {
      setProba({ ok: false, message: porukaGreske(e, 'Provera nije uspela.'), params: {} });
    } finally {
      setProbaRadi(false);
    }
  };

  const sacuvaj = async () => {
    if (!spremno) return;
    setRadi(true);
    setGreska(null);
    const postbackMapping = saljePotvrde ? mapiranje() : null;
    try {
      if (mreza) {
        await api.put(`/admin/affiliate/networks/${mreza.id}`, {
          name: name.trim(), postbackMapping, defaultCurrency: valuta, notes, isActive: aktivna,
        });
        onGotovo(!m && postbackMapping
          ? 'Mreža je izmenjena. Za adresu za potvrde napravite novu tajnu (ikonica ključa).'
          : 'Mreža je izmenjena.');
      } else {
        const r = await api.post<{ network: NetworkView; secret?: string; postbackUrl?: string }>('/admin/affiliate/networks', {
          name: name.trim(), slug: slug.trim(), postbackMapping, defaultCurrency: valuta, notes,
        });
        onGotovo('Mreža je dodata.', r.secret && r.postbackUrl
          ? { naslov: `Adresa za potvrde - ${r.network.name}`, secret: r.secret, postbackUrl: r.postbackUrl }
          : undefined);
      }
    } catch (e) {
      setGreska(porukaGreske(e, 'Mreža nije sačuvana.'));
    } finally {
      setRadi(false);
    }
  };

  const izmeniRed = (kljuc: number, izmena: Partial<RedStatusa>) =>
    setRedovi((rs) => rs.map((r) => (r.kljuc === kljuc ? { ...r, ...izmena } : r)));

  const unos = (vrednost: string, set: (v: string) => void, placeholder?: string) => (
    <input value={vrednost} onChange={(e) => { set(e.target.value); setProba(null); }} placeholder={placeholder} style={{ ...input, ...mono }} />
  );

  return (
    <Dijalog naslov={mreza ? `Izmena mreže - ${mreza.name}` : 'Nova mreža'} onZatvori={onZatvori} sirina={720}>
      {greska && <div style={errorBox}><AlertTriangle size={16} /> {greska}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1fr', gap: 10 }}>
        <Polje label="Naziv" pomoc="Kako se partner zove kod nas (npr. Travelpayouts). Lokali ga ne vide.">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} style={input} data-testid="mreza-naziv" />
        </Polje>
        <Polje label="Oznaka" pomoc={mreza ? 'Oznaka se ne menja.' : 'Kratko ime: mala slova, cifre i crtica (2-40). Posle čuvanja se ne menja.'}>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            disabled={!!mreza}
            style={{ ...input, ...mono, borderColor: !mreza && slug && !slugOk ? '#ef4444' : 'var(--color-border)' }}
            data-testid="mreza-oznaka"
          />
        </Polje>
        <Polje label="Valuta" pomoc="U kojoj valuti partner plaća proviziju. Važi kad potvrda ne kaže valutu.">
          <select value={valuta} onChange={(e) => { setValuta(e.target.value as Valuta); setProba(null); }} style={input} data-testid="mreza-valuta">
            {VALUTE.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Polje>
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, margin: '4px 0 12px' }}>
        <input type="checkbox" checked={saljePotvrde} onChange={(e) => setSaljePotvrde(e.target.checked)} style={{ marginTop: 3 }} data-testid="mreza-salje-potvrde" />
        <span>
          <strong style={{ color: 'var(--color-text-strong)' }}>Mreža javlja prodaju (postback)</strong>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)' }}>
            Isključite za partnera koji ima samo link: njegove ponude su „poseban link za svaki lokal", a zaradu unosite ručno po lokalu.
          </span>
        </span>
      </label>

      {saljePotvrde && (
        <>
          <h4 style={{ margin: '4px 0 6px', fontSize: 13, color: 'var(--color-text-strong)' }}>Polja u potvrdi prodaje</h4>
          <div style={{ ...napomena, marginBottom: 10 }}>
            Upisujete <strong>imena parametara</strong> koje mreža šalje (GET upit ili POST telo), ne njihove vrednosti.
            Ista imena koristite u adresi za potvrde koju unosite u panel mreže. Obavezna su prva tri.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <Polje label="Oznaka klika *" pomoc={POMOC_POLJA.clickId}>{unos(clickId, setClickId)}</Polje>
            <Polje label="Transakcija *" pomoc={POMOC_POLJA.txId}>{unos(txId, setTxId)}</Polje>
            <Polje label="Stanje *" pomoc={POMOC_POLJA.status}>{unos(status, setStatus)}</Polje>
            <Polje label="Iznos provizije" pomoc={POMOC_POLJA.amount}>{unos(amount, setAmount, 'nije obavezno')}</Polje>
            <Polje label="Valuta" pomoc={POMOC_POLJA.currency}>{unos(currency, setCurrency, 'nije obavezno')}</Polje>
            <Polje label="Vreme prodaje" pomoc={POMOC_POLJA.occurredAt}>{unos(occurredAt, setOccurredAt, 'nije obavezno')}</Polje>
          </div>

          <h4 style={{ margin: '4px 0 6px', fontSize: 13, color: 'var(--color-text-strong)' }}>Mapa stanja</h4>
          <div style={{ ...napomena, marginBottom: 10 }}>
            Levo: vrednost stanja tačno kako je mreža šalje (npr. <code>processing</code>, <code>paid</code>, <code>cancelled</code>).
            Desno: šta to znači kod nas. Nepoznato stanje se odbija, pa navedite sve vrednosti koje mreža koristi.
            „Odobrena" odmah ulazi u kredit lokala; „Naplaćeno od mreže" otvara isplatu u novcu.
          </div>
          {redovi.map((r) => (
            <div key={r.kljuc} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <input value={r.spolja} onChange={(e) => { izmeniRed(r.kljuc, { spolja: e.target.value }); setProba(null); }} placeholder="vrednost mreže, npr. confirmed" style={{ ...input, ...mono }} />
              <select value={r.nas} onChange={(e) => { izmeniRed(r.kljuc, { nas: e.target.value as ConversionStatus }); setProba(null); }} style={input}>
                {(Object.keys(STATUS_PRODAJE) as ConversionStatus[]).map((st) => <option key={st} value={st}>{STATUS_PRODAJE[st].label}</option>)}
              </select>
              <button onClick={() => setRedovi((rs) => rs.filter((x) => x.kljuc !== r.kljuc))} title="Ukloni red" style={btnIcon}><Trash2 size={16} /></button>
            </div>
          ))}
          {dupli && <div style={{ fontSize: 12, color: '#fca5a5', marginBottom: 8 }}>Ista vrednost mreže je navedena dva puta.</div>}
          <button
            onClick={() => setRedovi((rs) => [...rs, { kljuc: Math.max(-1, ...rs.map((x) => x.kljuc)) + 1, spolja: '', nas: 'pending' }])}
            style={{ ...btnSecondary, marginBottom: 12 }}
          >
            <Plus size={16} /> Dodaj vrednost
          </button>

          <h4 style={{ margin: '4px 0 6px', fontSize: 13, color: 'var(--color-text-strong)' }}>Probna potvrda</h4>
          <Polje
            label="Primer potvrde"
            pomoc="Nalepite adresu koju mreža šalje (ili samo parametre: sub_id=abc&state=paid, ili JSON). Ništa se ne upisuje - samo vidite kako bi Efikasa pročitala potvrdu."
          >
            <textarea
              value={primer}
              onChange={(e) => { setPrimer(e.target.value); setProba(null); }}
              rows={2}
              placeholder="https://api.efikasa.rs/api/affiliate/postback/…?sub_id=abc&action_id=123&state=paid&profit=2.50"
              style={{ ...input, ...mono, resize: 'vertical' }}
              data-testid="mreza-primer"
            />
          </Polje>
          <button onClick={() => void proveri()} disabled={!mapiranjeOk || !primer.trim() || probaRadi} style={{ ...btnSecondary, marginBottom: 10, opacity: !mapiranjeOk || !primer.trim() ? 0.5 : 1 }} data-testid="mreza-proveri">
            <Check size={16} /> {probaRadi ? 'Proveravam...' : 'Proveri primer'}
          </button>
          {proba && <ProbaPrikaz proba={proba} />}
        </>
      )}

      <Polje label="Beleška" pomoc="Samo za administratore: ugovor, kontakt, kako partner plaća.">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={2000} style={{ ...input, fontFamily: 'inherit', resize: 'vertical' }} />
      </Polje>

      {mreza && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8 }}>
          <input type="checkbox" checked={aktivna} onChange={(e) => setAktivna(e.target.checked)} />
          Aktivna
        </label>
      )}

      {!mreza && saljePotvrde && (
        <div style={napomena}>
          Posle čuvanja dobijate adresu za potvrde sa tajnom. Prikazuje se <strong>samo jednom</strong>.
        </div>
      )}

      <DugmadDijaloga onOtkazi={onZatvori} onPotvrdi={() => void sacuvaj()} radi={radi} onemoguceno={!spremno} />
    </Dijalog>
  );
}

function ProbaPrikaz({ proba }: { proba: ProbaRezultat }) {
  const primljeno = Object.entries(proba.params);
  return (
    <div style={{ ...(proba.ok ? okBox : errorBox), display: 'block', marginBottom: 12 }} data-testid="mreza-proba-rezultat">
      {proba.ok && proba.parsed ? (
        <>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Potvrda bi bila primljena:</div>
          <div style={{ color: 'var(--color-text)', lineHeight: 1.6 }}>
            klik <code>{proba.parsed.clickId}</code> · transakcija <code>{proba.parsed.txId}</code> · stanje{' '}
            <strong>{STATUS_PRODAJE[proba.parsed.status].label}</strong>
            {' · '}iznos {proba.parsed.amount !== null ? `${proba.parsed.amount} ${proba.parsed.currency ?? ''}` : <em>nije poslat</em>}
            {proba.parsed.occurredAt && ` · nastala ${trenutak(proba.parsed.occurredAt)}`}
          </div>
          {proba.currencySupported === false && (
            <div style={{ color: '#fca5a5', marginTop: 4 }}>Valuta {proba.parsed.currency} nije podržana - takva prodaja se upisuje, ali se ne pripisuje lokalu.</div>
          )}
          <div style={{ color: 'var(--color-text-muted)', marginTop: 4 }}>
            {proba.match
              ? <>Klik je pronađen: <strong>{proba.match.tenantName}</strong> · {proba.match.offerTitle}</>
              : 'Klik nije pronađen među skenovima - kod prave potvrde to znači „nesparena" prodaja (za izmišljen primer je u redu).'}
          </div>
        </>
      ) : (
        <div>{(proba.error && GRESKE_PROBE[proba.error]) || proba.message || 'Potvrda bi bila odbijena.'}</div>
      )}
      {primljeno.length > 0 && (
        <div style={{ color: 'var(--color-text-muted)', marginTop: 6, fontSize: 12 }}>
          Pročitani parametri: {primljeno.map(([k, v]) => `${k}=${v}`).join(' · ')}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════ Zahtevi ═════════════════════════

function ZahteviKartica({ admin }: { admin: boolean }) {
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const ucitaj = useCallback(async () => (await api.get<{ requests: RequestRow[] }>(`/admin/affiliate/requests?status=${filter}`)).requests, [filter]);
  const s = useUcitavanje(ucitaj);
  const [odluka, setOdluka] = useState<{ red: RequestRow; vrsta: 'approve' | 'reject' } | null>(null);
  const zahtevi = s.podaci ?? [];

  return (
    <div>
      <Traka onOsvezi={() => void s.osvezi()} radi={s.radi}>
        <select value={filter} onChange={(e) => setFilter(e.target.value as 'pending' | 'all')} style={{ ...input, width: 'auto' }}>
          <option value="pending">Čekaju odluku</option>
          <option value="all">Sve firme u programu</option>
        </select>
      </Traka>
      <Poruke greska={s.greska} poruka={s.poruka} />

      <div style={card}>
        {zahtevi.length === 0 ? (
          <div style={prazno}>{s.radi ? 'Učitavanje...' : filter === 'pending' ? 'Nema zahteva na čekanju.' : 'Nijedna firma još nije u programu.'}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tabela}>
              <thead>
                <tr>
                  <th style={th}>Firma</th>
                  <th style={th}>PIB</th>
                  <th style={th}>Stanje</th>
                  <th style={th}>Zatraženo</th>
                  <th style={th}>Odlučeno</th>
                  <th style={th}>Napomena firme</th>
                  <th style={th}>Razlog</th>
                  {admin && <th style={{ ...th, textAlign: 'right' }}>Radnje</th>}
                </tr>
              </thead>
              <tbody>
                {zahtevi.map((r) => (
                  <tr key={r.tenantId}>
                    <td style={td}><Link to={`/tenants/${r.tenantId}`}>{r.tenantName}</Link></td>
                    <td style={td}>{r.taxId || '-'}</td>
                    <td style={td}><Oznaka ton={STATUS_PROGRAMA[r.status].ton}>{STATUS_PROGRAMA[r.status].label}</Oznaka></td>
                    <td style={td}>{trenutak(r.requestedAt)}</td>
                    <td style={td}>{trenutak(r.decidedAt)}</td>
                    <td style={td}>{r.note || '-'}</td>
                    <td style={td}>{r.reason || '-'}</td>
                    {admin && (
                      <td style={{ ...td, textAlign: 'right' }}>
                        {r.status === 'pending' && (
                          <div style={{ display: 'inline-flex', gap: 6 }}>
                            <button onClick={() => setOdluka({ red: r, vrsta: 'approve' })} style={btnSecondary}>Prihvati</button>
                            <button onClick={() => setOdluka({ red: r, vrsta: 'reject' })} style={btnDangerSecondary}>Odbij</button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {odluka && (
        <OdlukaOZahtevu
          tenantId={odluka.red.tenantId}
          tenantName={odluka.red.tenantName}
          isFlatRate={odluka.red.isFlatRate}
          vrsta={odluka.vrsta}
          onZatvori={() => setOdluka(null)}
          onGotovo={(p) => { s.setPoruka(p); void s.osvezi(); }}
        />
      )}
    </div>
  );
}

/**
 * Uključivanje programa (prihvatanje zahteva ili aktivacija) - uvek uz izbor
 * načina nagrade za tu firmu, jer globalnog podešavanja nema (ADR-078).
 */
export function UkljucivanjeDijalog({ tenantId, tenantName, isFlatRate, vrsta, pocetniNacin, onZatvori, onGotovo }: {
  tenantId: string; tenantName: string; isFlatRate: boolean; vrsta: 'approve' | 'activate';
  pocetniNacin?: RewardMode | null; onZatvori: () => void; onGotovo: (poruka: string) => void;
}) {
  const [nacin, setNacin] = useState<RewardMode | null>(isFlatRate ? 'credits' : pocetniNacin ?? null);
  const [napomena, setNapomena] = useState('');
  const [greska, setGreska] = useState<string | null>(null);
  const [radi, setRadi] = useState(false);

  const potvrdi = async () => {
    if (!nacin) return;
    setRadi(true);
    setGreska(null);
    try {
      await api.post(`/admin/tenants/${tenantId}/affiliate/${vrsta}`, {
        rewardMode: nacin, ...(napomena.trim() ? { reason: napomena.trim() } : {}),
      });
      onZatvori();
      onGotovo(`Program je uključen za „${tenantName}" - nagrada: ${NACIN_NAGRADE[nacin].toLowerCase()}.`);
    } catch (e) {
      setGreska(porukaGreske(e, 'Program nije uključen.'));
    } finally {
      setRadi(false);
    }
  };

  return (
    <Dijalog naslov={vrsta === 'approve' ? 'Prihvati zahtev' : 'Aktiviraj program'} onZatvori={onZatvori} sirina={540}>
      {greska && <div style={errorBox}><AlertTriangle size={16} /> {greska}</div>}
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--color-text)', lineHeight: 1.6 }}>
        Uključiti partnerske ponude za „{tenantName}"? Firma dobija obaveštenje i ponude odmah postaju dostupne.
      </p>
      <Polje label="Način nagrade za ovu firmu (obavezno)">
        <IzborNacina vrednost={nacin} onIzbor={setNacin} pausalac={isFlatRate} />
      </Polje>
      {vrsta === 'activate' && (
        <Polje label="Napomena (nije obavezno)">
          <textarea value={napomena} onChange={(e) => setNapomena(e.target.value)} rows={2} maxLength={500} style={{ ...input, fontFamily: 'inherit', resize: 'vertical' }} />
        </Polje>
      )}
      <DugmadDijaloga onOtkazi={onZatvori} onPotvrdi={() => void potvrdi()} potvrdi={vrsta === 'approve' ? 'Prihvati' : 'Aktiviraj'} radi={radi} onemoguceno={!nacin} />
    </Dijalog>
  );
}

export function OdlukaOZahtevu({ tenantId, tenantName, isFlatRate, vrsta, onZatvori, onGotovo }: {
  tenantId: string; tenantName: string; isFlatRate: boolean; vrsta: 'approve' | 'reject'; onZatvori: () => void; onGotovo: (poruka: string) => void;
}) {
  if (vrsta === 'approve') {
    return <UkljucivanjeDijalog tenantId={tenantId} tenantName={tenantName} isFlatRate={isFlatRate} vrsta="approve" onZatvori={onZatvori} onGotovo={onGotovo} />;
  }
  return (
    <ConfirmDialog
      title="Odbij zahtev"
      message={`Odbiti zahtev firme „${tenantName}"? Firma vidi razlog i može ponovo da zatraži pristup.`}
      confirmLabel="Odbij"
      input={{ label: 'Razlog (obavezno, firma ga vidi)', required: true, multiline: true }}
      onConfirm={async (razlog) => {
        try {
          await api.post(`/admin/tenants/${tenantId}/affiliate/reject`, { reason: (razlog ?? '').trim() });
        } catch (e) {
          throw new Error(porukaGreske(e));
        }
        onGotovo(`Zahtev firme „${tenantName}" je odbijen.`);
      }}
      onClose={onZatvori}
    />
  );
}

// ═════════════════════════ Prodaje ═════════════════════════

type Firma = { id: string; name: string };

const PRESKOCENO: Record<string, string> = {
  NOT_FOUND: 'nije pronađena',
  REVERSED: 'poništena',
  ALREADY_PAID: 'već naplaćena',
  NOT_APPROVED: 'mreža je još nije odobrila',
};

function ProdajeKartica({ admin }: { admin: boolean }) {
  const [stanje, setStanje] = useState<'' | ConversionStatus>('');
  const [mrezaId, setMrezaId] = useState('');
  const [firmaId, setFirmaId] = useState('');
  const [nesparene, setNesparene] = useState(false);
  const [bezKursa, setBezKursa] = useState(false);
  const [mreze, setMreze] = useState<NetworkView[]>([]);
  const [firme, setFirme] = useState<Firma[]>([]);
  const [izbor, setIzbor] = useState<Set<string>>(new Set());
  const [potvrda, setPotvrda] = useState(false);

  useEffect(() => {
    api.get<{ networks: NetworkView[] }>('/admin/affiliate/networks').then((r) => setMreze(r.networks)).catch(() => { /* filter je pomoć, ne uslov */ });
    api.get<any>('/admin/tenants')
      .then((t) => setFirme((Array.isArray(t) ? t : (t.rows ?? [])).map((x: Firma) => ({ id: x.id, name: x.name }))))
      .catch(() => { /* filter je pomoć, ne uslov */ });
  }, []);

  const upit = useMemo(() => {
    const q = new URLSearchParams();
    if (stanje) q.set('status', stanje);
    if (mrezaId) q.set('networkId', mrezaId);
    if (firmaId && !nesparene) q.set('tenantId', firmaId);
    if (nesparene) q.set('unmatched', 'true');
    if (bezKursa) q.set('needsFxRate', 'true');
    q.set('limit', '500');
    return q.toString();
  }, [stanje, mrezaId, firmaId, nesparene, bezKursa]);

  const ucitaj = useCallback(async () => (await api.get<{ conversions: ConversionView[] }>(`/admin/affiliate/conversions?${upit}`)).conversions, [upit]);
  const s = useUcitavanje(ucitaj);
  const prodaje = s.podaci ?? [];

  useEffect(() => { setIzbor(new Set()); }, [upit]);

  const odobrene = prodaje.filter((p) => p.state === 'approved');
  const bezKursaBroj = prodaje.filter((p) => p.needsFxRate).length;
  const nepodrzanaBroj = prodaje.filter((p) => p.unsupportedCurrency).length;

  const oznaciNaplaceno = async () => {
    const items = [...izbor].map((k) => {
      const [networkId, ...ostatak] = k.split('|');
      return { networkId, externalTxId: ostatak.join('|') };
    });
    let r: { marked: number; skipped: Array<{ networkId: string; externalTxId: string; reason: string }> };
    try {
      r = await api.post('/admin/affiliate/conversions/mark-paid', { items });
    } catch (e) {
      throw new Error(porukaGreske(e));
    }
    const preskoceno = r.skipped.length
      ? ` Preskočeno ${r.skipped.length}: ${r.skipped.slice(0, 5).map((x) => `${x.externalTxId} (${PRESKOCENO[x.reason] ?? x.reason})`).join(', ')}${r.skipped.length > 5 ? '…' : ''}.`
      : '';
    s.setPoruka(`Označeno kao naplaćeno od mreže: ${r.marked}.${preskoceno}`);
    setIzbor(new Set());
    void s.osvezi();
  };

  return (
    <div>
      <Traka onOsvezi={() => void s.osvezi()} radi={s.radi}>
        <select value={stanje} onChange={(e) => setStanje(e.target.value as '' | ConversionStatus)} style={{ ...input, width: 'auto' }}>
          <option value="">Sva stanja</option>
          {(Object.keys(STATUS_PRODAJE) as ConversionStatus[]).map((st) => <option key={st} value={st}>{STATUS_PRODAJE[st].label}</option>)}
        </select>
        <select value={mrezaId} onChange={(e) => setMrezaId(e.target.value)} style={{ ...input, width: 'auto' }}>
          <option value="">Sve mreže</option>
          {mreze.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select value={firmaId} onChange={(e) => setFirmaId(e.target.value)} disabled={nesparene} style={{ ...input, width: 'auto', maxWidth: 220 }}>
          <option value="">Sve firme</option>
          {firme.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={nesparene} onChange={(e) => setNesparene(e.target.checked)} /> Samo nesparene
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={bezKursa} onChange={(e) => setBezKursa(e.target.checked)} /> Samo bez kursa
        </label>
      </Traka>
      <Poruke greska={s.greska} poruka={s.poruka} />

      {bezKursaBroj > 0 && (
        <div style={warnBox}>
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>{bezKursaBroj} prodaja u stranoj valuti čeka kurs i ne pripisuje se lokalu dok se kurs ne unese (kartica Kursevi).</div>
        </div>
      )}
      {nepodrzanaBroj > 0 && (
        <div style={warnBox}>
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            {nepodrzanaBroj} prodaja je javljeno u valuti van RSD/EUR/USD i ne pripisuje se lokalu. Proverite sa mrežom da
            proviziju javlja u podržanoj valuti; za već javljene prodaje zaradu unesite ručno na kartici klijenta.
          </div>
        </div>
      )}

      <div style={{ ...card, padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
            {s.radi ? 'Učitavanje...' : `Prodaja: ${prodaje.length}${prodaje.length >= 500 ? ' (prikazano najviše 500)' : ''}`}
          </span>
          {admin && odobrene.length > 0 && (
            <button
              onClick={() => setIzbor(izbor.size === odobrene.length ? new Set() : new Set(odobrene.map((p) => `${p.networkId}|${p.externalTxId}`)))}
              style={btnSecondary}
            >
              {izbor.size === odobrene.length ? 'Poništi izbor' : `Izaberi sve odobrene (${odobrene.length})`}
            </button>
          )}
          <div style={{ flex: 1 }} />
          {admin && izbor.size > 0 && (
            <button onClick={() => setPotvrda(true)} style={btnPrimary} data-testid="affiliate-mark-paid">
              <Check size={16} /> Označi naplaćeno od mreže ({izbor.size})
            </button>
          )}
        </div>
        <ProdajeTabela
          prodaje={prodaje}
          izbor={admin ? izbor : undefined}
          onIzbor={admin ? (k, da) => setIzbor((st) => { const n = new Set(st); if (da) n.add(k); else n.delete(k); return n; }) : undefined}
        />
      </div>

      {potvrda && (
        <ConfirmDialog
          title="Naplaćeno od mreže"
          message={`Potvrdite da je mreža Efikasi isplatila proviziju za ${izbor.size} izabranih prodaja. Za lokale sa isplatom u novcu ovaj iznos time postaje raspoloživ za isplatu. Upis se ne može poništiti.`}
          confirmLabel="Označi naplaćeno"
          variant="default"
          onConfirm={oznaciNaplaceno}
          onClose={() => setPotvrda(false)}
        />
      )}
    </div>
  );
}

// ═════════════════════════ Isplate ═════════════════════════

type FilterIsplata = 'open' | 'requested' | 'approved' | 'rejected' | 'paid' | '';

function IsplateKartica({ admin }: { admin: boolean }) {
  const [filter, setFilter] = useState<FilterIsplata>('open');
  const ucitaj = useCallback(async () => (await api.get<{ payouts: PayoutView[] }>(`/admin/affiliate/payouts${filter ? `?status=${filter}` : ''}`)).payouts, [filter]);
  const s = useUcitavanje(ucitaj);
  const [radnja, setRadnja] = useState<IsplataRadnja | null>(null);

  return (
    <div>
      <Traka onOsvezi={() => void s.osvezi()} radi={s.radi}>
        <select value={filter} onChange={(e) => setFilter(e.target.value as FilterIsplata)} style={{ ...input, width: 'auto' }}>
          <option value="open">Otvorene (zatražene i odobrene)</option>
          <option value="requested">Zatražene</option>
          <option value="approved">Odobrene</option>
          <option value="paid">Isplaćene</option>
          <option value="rejected">Odbijene</option>
          <option value="">Sve</option>
        </select>
      </Traka>
      <Poruke greska={s.greska} poruka={s.poruka} />
      <div style={{ ...napomena, marginBottom: 14 }}>
        Lokal sme da podigne samo svoj deo provizije za prodaje koje je mreža Efikasi stvarno platila, umanjeno za ranije
        isplate i poništenja. Novac se šalje van sistema; ovde se beleži odluka i referenca uplate.
      </div>
      <div style={{ ...card, padding: 0 }}>
        {s.radi && !s.podaci ? <div style={prazno}>Učitavanje...</div> : (
          <IsplateTabela isplate={s.podaci ?? []} onRadnja={admin ? setRadnja : undefined} />
        )}
      </div>
      {radnja && (
        <IsplataPotvrda
          radnja={radnja}
          onZatvori={() => setRadnja(null)}
          onGotovo={(p) => { s.setPoruka(p); void s.osvezi(); }}
        />
      )}
    </div>
  );
}

// ═════════════════════════ Kursevi ═════════════════════════

function danas(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function KurseviKartica({ admin }: { admin: boolean }) {
  const [filterValuta, setFilterValuta] = useState('');
  const ucitaj = useCallback(async () => (
    (await api.get<{ rates: FxRate[] }>(`/admin/affiliate/fx-rates${filterValuta ? `?currency=${filterValuta}` : ''}`)).rates
  ), [filterValuta]);
  const s = useUcitavanje(ucitaj);
  const [valuta, setValuta] = useState<Valuta>('EUR');
  const [dan, setDan] = useState(danas());
  const [kurs, setKurs] = useState('');
  const [radi, setRadi] = useState(false);

  const kursBroj = Number(kurs.replace(',', '.'));
  const spremno = valuta !== 'RSD' && /^\d{4}-\d{2}-\d{2}$/.test(dan) && kurs.trim() !== '' && Number.isFinite(kursBroj) && kursBroj > 0;

  const unesi = async () => {
    if (!spremno) return;
    setRadi(true);
    s.setGreska(null);
    s.setPoruka(null);
    try {
      await api.post('/admin/affiliate/fx-rates', { currency: valuta, rateDate: dan, rateToRsd: kurs.trim().replace(',', '.') });
      s.setPoruka(`Kurs ${valuta} za ${datum(dan)} je unet.`);
      setKurs('');
      await s.osvezi();
    } catch (e) {
      s.setGreska(porukaGreske(e, 'Kurs nije unet.'));
    } finally {
      setRadi(false);
    }
  };

  return (
    <div>
      <Poruke greska={s.greska} poruka={s.poruka} />

      {admin && (
        <div style={{ ...card, marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)' }}>Ručni unos kursa</h3>
          <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
            Server sam preuzima srednji kurs NBS za EUR i USD (na 6 sati, za poslednjih 7 dana; izvor „NBS" u tabeli).
            Ručno unesite samo kurs koji nedostaje. Kurs se unosi jednom i <strong>ne menja se</strong> - iz njega se izvodi već pripisana zarada lokala.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 180px 200px auto', gap: 10, alignItems: 'end' }}>
            <Polje label="Valuta">
              <select value={valuta} onChange={(e) => setValuta(e.target.value as Valuta)} style={input}>
                {VALUTE.filter((v) => v !== 'RSD').map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </Polje>
            <Polje label="Datum">
              <input type="date" value={dan} onChange={(e) => setDan(e.target.value)} style={input} />
            </Polje>
            <Polje label="1 jedinica = RSD">
              <input value={kurs} onChange={(e) => setKurs(e.target.value)} inputMode="decimal" placeholder="npr. 117,1523" style={input} />
            </Polje>
            <div style={{ marginBottom: 12 }}>
              <button onClick={() => void unesi()} disabled={!spremno || radi} style={{ ...btnPrimary, opacity: !spremno || radi ? 0.5 : 1 }}>
                <Save size={16} /> {radi ? 'Čuvanje...' : 'Unesi kurs'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Traka onOsvezi={() => void s.osvezi()} radi={s.radi}>
        <select value={filterValuta} onChange={(e) => setFilterValuta(e.target.value)} style={{ ...input, width: 'auto' }}>
          <option value="">Sve valute</option>
          {VALUTE.filter((v) => v !== 'RSD').map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </Traka>

      <div style={card}>
        {(s.podaci ?? []).length === 0 ? (
          <div style={prazno}>{s.radi ? 'Učitavanje...' : 'Nema unetih kurseva.'}</div>
        ) : (
          <table style={tabela}>
            <thead>
              <tr>
                <th style={th}>Datum</th>
                <th style={th}>Valuta</th>
                <th style={{ ...th, textAlign: 'right' }}>Kurs (RSD)</th>
                <th style={th}>Izvor</th>
                <th style={th}>Uneto</th>
              </tr>
            </thead>
            <tbody>
              {(s.podaci ?? []).map((r) => (
                <tr key={`${r.currency}-${r.rateDate}`}>
                  <td style={td}>{datum(r.rateDate)}</td>
                  <td style={td}>{r.currency}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{r.rateToRsd}</td>
                  <td style={td}>{r.source === 'manual' ? 'ručno' : r.source === 'nbs' ? 'NBS' : r.source}</td>
                  <td style={td}>{trenutak(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════ Podešavanja ═════════════════════════

function PodesavanjaKartica({ admin }: { admin: boolean }) {
  const ucitaj = useCallback(() => api.get<AffiliateSettings>('/admin/affiliate/settings'), []);
  const s = useUcitavanje(ucitaj);
  const [prag, setPrag] = useState('');
  const [radi, setRadi] = useState(false);

  useEffect(() => {
    if (!s.podaci) return;
    setPrag(s.podaci.cashThresholdRsd);
  }, [s.podaci]);

  const pragBroj = Number(prag.replace(',', '.'));
  const pragOk = prag.trim() !== '' && Number.isFinite(pragBroj) && pragBroj >= 0;

  const sacuvaj = async () => {
    if (!pragOk) return;
    setRadi(true);
    s.setGreska(null);
    s.setPoruka(null);
    try {
      await api.put('/admin/affiliate/settings', { cashThresholdRsd: prag.trim().replace(',', '.') });
      s.setPoruka('Podešavanja su sačuvana.');
      await s.osvezi();
    } catch (e) {
      s.setGreska(porukaGreske(e, 'Podešavanja nisu sačuvana.'));
    } finally {
      setRadi(false);
    }
  };

  if (!s.podaci) {
    return (
      <div>
        <Poruke greska={s.greska} poruka={null} />
        {!s.greska && <div style={{ color: 'var(--color-text-muted)' }}>Učitavanje...</div>}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 760 }}>
      <Poruke greska={s.greska} poruka={s.poruka} />

      <div style={card}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)' }}>Isplata u novcu</h3>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
          Način nagrade (krediti ili novac) se bira za svaku firmu posebno - pri prihvatanju zahteva ili na kartici
          klijenta (Klijenti → firma → Partnerske ponude).
        </p>

        <Polje label="Prag isplate u novcu (RSD)" pomoc="Lokal ne može da zatraži isplatu manju od ovog iznosa.">
          <input value={prag} onChange={(e) => setPrag(e.target.value)} disabled={!admin} inputMode="decimal" style={{ ...input, maxWidth: 220, borderColor: pragOk ? 'var(--color-border)' : '#ef4444' }} />
        </Polje>

        {admin && (
          <button onClick={() => void sacuvaj()} disabled={radi || !pragOk} style={{ ...btnPrimary, opacity: radi || !pragOk ? 0.5 : 1 }}>
            <Save size={16} /> {radi ? 'Čuvanje...' : 'Sačuvaj'}
          </button>
        )}
      </div>

      <div style={card}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)' }}>Adrese</h3>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
          Podešavaju se na serveru (<code>AFFILIATE_PUBLIC_BASE_URL</code>), ovde se samo prikazuju.
        </p>
        <Polje label="Kratka adresa za QR kodove" pomoc="QR kod vodi na ovu adresu + /r/<oznaka>.">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ ...napomena, ...mono, color: 'var(--color-text-strong)', flex: 1 }}>{s.podaci.publicBaseUrl || '-'}</div>
            {s.podaci.publicBaseUrl && <KopirajDugme tekst={s.podaci.publicBaseUrl} />}
          </div>
        </Polje>
        <Polje label="Osnova adrese za potvrde mreža" pomoc="Pun postback URL sa tajnom dobija se pri dodavanju mreže ili zameni tajne.">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ ...napomena, ...mono, color: 'var(--color-text-strong)', flex: 1 }}>{s.podaci.postbackBaseUrl || '-'}</div>
            {s.podaci.postbackBaseUrl && <KopirajDugme tekst={s.podaci.postbackBaseUrl} />}
          </div>
        </Polje>
      </div>
    </div>
  );
}
